<template>
	<Loader v-if="loading" />
	<div class="xpage-content-page">
		<Page>
			<div class="xpage-breadcrumbs">
				<router-link to="/" class="icon-home"></router-link>
				<span>></span>
				<router-link :to="`/batches`">Batches</router-link>
			</div>
			<div class="xpage-disbursements">
				<div class="xpage-inner batch-edit-title">
					<h2>{{ pageName }}</h2>
				</div>
				<div class="batch-header-info xpage-inner">
					<div class="batch-header-info-item">
						<div>
							<div class="batch-header-info-label">
								<span class="batch-title">Batch ID:</span>
								{{ batchData?.disbursementBatch.id }}
							</div>
							<div class="batch-header-info-sub-value">
								<span class="batch-title">Created:</span>
								{{ batchData?.disbursementBatch.createdAtUtcFormatted }}
							</div>
						</div>
						<div>
							<div class="batch-header-info-value">
								<span class="batch-title">Approved By:</span>
								{{ batchData?.disbursementBatch.approvedBy }}
							</div>
							<div class="batch-header-info-value">
								<span class="batch-title">Approved Date:</span>
								{{ batchData?.disbursementBatch.approvedAtUtcFormatted }}
							</div>
						</div>
					</div>
				</div>
				<div class="date-card">
					<div class="batch-subheader-info">
						<div class="batch-subheader-info-item">
							<div class="batch-subheader-info-label">
								<span class="batch-title">Prefund:</span>
								{{ batchData?.disbursementBatch.prefundReportDateFormatted }}
							</div>
							<div class="batch-subheader-info-label">
								<span class="batch-title">Disb Rpt:</span>
								{{
									batchData?.disbursementBatch.disbursementReportDateFormatted
								}}
							</div>
							<div class="batch-subheader-info-label">
								<span class="batch-title">Acct Rpt:</span>
								{{ batchData?.disbursementBatch.accountingReportDateFormatted }}
							</div>
						</div>
					</div>
				</div>
				<div class="xpage-inner">
					<Loader v-if="loading" />
					<div>
						<!-- In/Out Columns -->
						<PermissionsWrapper
							:showDisabledIfNotVisible="true"
							:permissionIds="[Permissions.DisbursementBatches]"
							:permissionLevel="PermissionLevels.Update"
						>
							<table class="db-table">
								<thead>
									<tr>
										<th
											v-for="header in columnHeaders"
											:key="header.key"
											@click="toggleSort(header.key)"
											:class="{
												clickable_row: header.sortable,
												'padded-header':
													header.key === 'inBatch' || header.key === 'outBatch',
											}"
										>
											<div class="header-container">
												<div class="header-text">{{ header.name }}</div>
											</div>
											<div
												v-if="header.key === 'separator'"
												class="separator-container"
											></div>
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(disbursement, index) in sortedDisbursements"
										:key="index"
										@click="navigateToDisbursement(disbursement.referenceId)"
										class="clickableRow"
									>
										<td>
											<input
												v-bind:disabled="isApproved || disbursement.inBatch"
												class="radio-in"
												type="radio"
												:checked="disbursement.inBatch"
												@click.stop="
													handleDisbursementChange(disbursement, true)
												"
											/>
										</td>
										<td>
											<input
												v-bind:disabled="isApproved || !disbursement.inBatch"
												class="radio-out"
												type="radio"
												:checked="!disbursement.inBatch"
												@click.stop="
													handleDisbursementChange(disbursement, false)
												"
											/>
										</td>
										<td class="separator" />
										<td>
											{{ functions.getLenderName(disbursement.lenderId) }}
										</td>
										<td>{{ disbursement.borrowerName }}</td>
										<td>{{ disbursement.disbursedAmountFormatted }}</td>
										<td>{{ disbursement.disbursementMethod }}</td>
										<td>{{ disbursement.transactionDateFormatted }}</td>
										<td>{{ disbursement.referenceId }}</td>
										<td>{{ disbursement.lenderUseOnly }}</td>
										<td>{{ disbursement.sequenceNumber }}</td>
									</tr>
								</tbody>
							</table>
						</PermissionsWrapper>
					</div>
					<div class="disbursement-buttons-container">
						<PermissionsButton
							:permissionIds="[Permissions.DisbursementBatches]"
							:permissionLevel="PermissionLevels.Update"
							@click="goBack"
							class="xedit"
						>
							Back
						</PermissionsButton>
						<PermissionsButton
							v-bind:disabled="isApproved"
							:permissionIds="[Permissions.DisbursementBatches]"
							:permissionLevel="PermissionLevels.Update"
							@click="approveDisbursements"
							class="xedit"
						>
							Approve
						</PermissionsButton>
					</div>
				</div>
			</div>
		</Page>
	</div>
</template>

<script setup lang="ts">
import Loader from "@/components/Loader.vue";
import Page from "@/components/layout/Page.vue";
import store from "@/store";
import { useRoute } from "vue-router";
import { onMounted, ref, computed, onUnmounted } from "vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import PermissionsButton from "@/components/wrappers/PermissionsButton.vue";
import { useBatchesStore } from "@/store/store/batchesStore";
import axios from "axios";
import functions from "@/use/functions";
import useFunctions from "@/use/functions1";

const { decodeHTMLEncodedStr } = useFunctions();

const batchesStore = useBatchesStore();
const loading = ref(false);
const pageName = "Edit Disbursement Batch";
const route = useRoute();

const batchData = ref<{
	disbursementBatch: any;
	disbursements: any[];
} | null>(null);

const batchId = computed(() => batchesStore.getBatchId());
const ascending = ref<boolean>(true);
const sortColumn = ref<string>("");

const isApproved = computed(() => {
	return batchData.value?.disbursementBatch.approved === true;
});

const goBack = () => {
	window.history.back();
};

const firstName = computed(() => {
	return decodeHTMLEncodedStr(store.state.oidcStore.user.given_name);
});

const lastName = computed(() => {
	return decodeHTMLEncodedStr(store.state.oidcStore.user.family_name);
});

const navigateToDisbursement = (referenceId: string) => {
	// ! Just for testing purposes in case we actually don't' to open the disbursement in a new tab
	// router.push(`/loans/${referenceId}`);
	const url = `/loans/${referenceId}`;
	window.open(url, "_blank");
};

const columnHeaders = computed(() => {
	return [
		{ key: "inBatch", name: "In", sortable: true },
		{ key: "outBatch", name: "Out", sortable: true },
		{ key: "separator", name: "", sortable: false },
		{ key: "lenderName", name: "Credit Union", sortable: true },
		{ key: "borrowerName", name: "Borrower ( F/L )", sortable: true },
		{
			key: "disbursedAmountFormatted",
			name: "Sch Disb Amt",
			sortable: true,
		},
		{ key: "disbursementMethod", name: "Disb Method", sortable: true },
		{ key: "transactionDateFormatted", name: "Disb Date", sortable: true },
		{ key: "referenceId", name: "Reference Id#", sortable: true },
		{
			key: "lenderUseOnly",
			name: "Lender Use (LoanId + DrawId + Disb Id#)",
			sortable: true,
		},
		{ key: "sequenceNumber", name: "Disb Seq Id#", sortable: true },
	];
});

const sortedDisbursements = computed(() => {
	if (!batchData.value?.disbursements) return [];
	const disbursements = [...batchData.value.disbursements];
	disbursements.sort((a, b) => {
		const key = sortColumn.value as keyof typeof a;
		let aValue = a[key];
		let bValue = b[key];

		if (key === "outBatch") {
			aValue = !a.inBatch;
			bValue = !b.inBatch;
		}

		if (typeof aValue === "number" && typeof bValue === "number") {
			return ascending.value ? aValue - bValue : bValue - aValue;
		} else if (typeof aValue === "string" && typeof bValue === "string") {
			return ascending.value
				? aValue.localeCompare(bValue)
				: bValue.localeCompare(aValue);
		} else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
			if (aValue !== bValue) {
				return ascending.value ? (aValue ? 1 : -1) : aValue ? -1 : 1;
			}
		}
		return 0;
	});
	return disbursements;
});

async function handleDisbursementChange(disbursement: any, add: boolean) {
	try {
		if (add) {
			await axios.post(
				`/api/disbursementbatches/${batchId.value}/disbursement/${disbursement.id}`
			);
			console.log("Add disbursement response:");
			disbursement.inBatch = true;
		} else {
			await axios.delete(
				`/api/disbursementbatches/${batchId.value}/disbursement/${disbursement.id}`
			);
			disbursement.inBatch = false;
		}
	} catch (error) {
		console.error("failed to remove disbursement from batch:", error);
	}
}

async function approveDisbursements() {
	if (batchData.value) {
		try {
			const approvedByUserName = `${firstName.value}${lastName.value}`;
			const response = await axios.post(
				`/api/disbursementbatches/${batchId.value}/approve`,
				{
					batchId: batchId.value,
					approvedByUserId: store.state.oidcStore.user.sub,
					approvedByUserName: approvedByUserName,
				}
			);

			console.log("Approval response:", response.data);
			functions.openModal({
				title: "Success!",
				description: "The disbursement batch has been saved",
			});
			goBack();
		} catch (error) {
			functions.openModal({
				title: "Error",
				description:
					"There was an issue approving the disbursement batch. Please verify that it has not been approved already.",
			});
		}
	} else {
		console.error("batchData.value is null");
	}
}

function toggleSort(column: string) {
	if (sortColumn.value === column) {
		ascending.value = !ascending.value;
	} else {
		sortColumn.value = column;
		ascending.value = true;
	}
}

onMounted(async () => {
	const batchNumber = route.params.batchNumber;
	if (!batchNumber) {
		console.error("batchNumber is null");
		return;
	}
	try {
		const response = await axios.get(`/api/disbursementbatches/${batchNumber}`);
		batchData.value = response.data;
		batchesStore.setBatchData(batchData.value);
	} catch (error) {
		console.error("failed to load batch data:", error);
	}
});

onUnmounted(() => {
	batchesStore.clearBatchData();
});
</script>

<style scoped>
.batch-subheader-info-item {
	display: flex;
	justify-content: space-between;
	font-size: 16px;
	padding: 10px 0;
}

.batch-edit-title {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-top: 25px;
}

.batch-header-info-item {
	font-size: 20px;
	display: flex;
	justify-content: space-between;
}

.batch-header-info {
	justify-content: space-between;
	margin-bottom: 10px;
	padding-left: 20px;
}

.batch-title {
	font-weight: 700;
	padding-right: 5px;
}

.clickableRow:hover {
	background-color: #d4d6dd;
}

.date-card {
	border: 1px solid #d4d6dd;
	padding: 10px;
	border-radius: 5px;
	margin: 20px;
}

.disbursement-buttons-container {
	margin-top: 20px;
}

.header-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.header-container:hover {
	visibility: visible;
}

.header-text {
	flex-grow: 1;
	text-align: center;
}

input[type="radio"] {
	margin: 0;
	font: inherit;
	width: 25px;
	height: 25px;
	border-radius: 100%;
	transform: translateY(-0.075em);
}

input:checked {
	border: 16px solid #333333;
}

.padded-header {
	padding-left: 8px;
}

.separator {
	padding: 10px;
	border: none;
	background-color: #ecedef;
	pointer-events: none;
}

table {
	width: 100%;
	border-collapse: collapse;
}

thead {
	background-color: #f8f8f8;
	text-transform: capitalize;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
}

th {
	cursor: pointer;
	padding: 5px;
}

thead th:nth-child(3) {
	border: none;
	background-color: #ecedef;
	pointer-events: none;
}

th,
td {
	padding: 8px;
	border: 1px solid #ddd;
	text-align: center;
}

tr:nth-child(even) {
	background-color: #f2f2f2;
}

table th:hover {
	background: #333;
	color: white;
}

.xpage-content-page {
	padding-left: 20px;
}

.xpage-disbursements {
	font-family: "Montserrat", arial, sans-serif;
}

.xedit {
	margin: 5px;
}
</style>
